import {
    Component,
    effect,
    ElementRef,
    HostListener,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SmartAppBannerComponent } from '../smart-app-banner/smart-app-banner.component';
import { EditProfileComponent } from '../edit-profile/edit-profile.component';
import { environment } from '../../../environments/environment';
import { MyStoriesService } from '../../modules/stories/services/my-stories.service';
import { AwsAuthService } from '../../services/aws-auth.service';
import { LanguageService } from '../../services/language.service';
import { TranslationService } from '../../services/translation.service';
import { ProfileDetails } from '../interfaces/myStories.interface';
import {
    debounceTime,
    filter,
    fromEvent,
    map,
    Subject,
    takeUntil,
} from 'rxjs';

import { CountrySelectionService } from '../../services/country-selection/country-selection.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports:  [TranslateModule, CommonModule, SmartAppBannerComponent, EditProfileComponent],
    standalone: true
})
export class HeaderComponent implements OnInit {
    menuSelected = false;
    currentUser: any;
    showHeader: any = true;
    selectedCategory: string = '/home';
    toggleHeader: any = environment.isEnabled;
    translationData = [];
    language = this.languageService.getLanguage();
    showMenu = false;
    selectedLang: any;
    showBottomMenu: boolean = false;
    screenWidth: any;
    isHeaderMin = false;
    isProfileClicked = false;
    videoplayed: any = 'false';
    featureFlags: any;
    isProd = environment.production;
    public showMyProfileLink: boolean = false;
    public showLoginLink: boolean = false;
    languageToDisplay: string = 'Deutsch';
    showLangPicker: boolean = false;
    secondLastUrlElement: string = '';
    profileInfo: ProfileDetails = <any>null;
    profileImage = '../../../../../assets/images/profile-fallback.png';
    isMyProfileVisible: boolean = false;
    isLanguageSelector: boolean = false;
    public isHeadingAvailable: boolean = false;
    public isMyStoriesVisible: boolean = false;
    public enableMyProfile: boolean = false;
    showLogout: boolean = false;
    showSmartAppBanner: boolean = true;
    isAtTop: boolean = true;
    hoverHeader: any = true;
    scrollSubscription: any;
    lastScrollTop: number = 0;
    private destroy$ = new Subject<void>();
    isSmartBannerVisible: boolean = true;
    bannerOriginalHeight: number = 0;
    bannerCurrentHeight: number | null = null;

    @ViewChild('bannerArea') set bannerAreaRef(el: ElementRef | undefined) {
        if (el && this.bannerOriginalHeight === 0) {
            this.bannerCurrentHeight = null;
            requestAnimationFrame(() => {
                this.bannerOriginalHeight = el.nativeElement.offsetHeight;
                this.bannerCurrentHeight = this.bannerOriginalHeight;
            });
        } else if (!el) {
            this.bannerOriginalHeight = 0;
            this.bannerCurrentHeight = 0;
        }
        this._bannerAreaEl = el;
    }
    private _bannerAreaEl: ElementRef | undefined;

    // Listen for scroll events on the window
    @HostListener('window:scroll', [])
    onWindowScroll() {
        this.checkScrollPosition();
    }

    @HostListener('window:resize')
    onResize() {
        if (this._bannerAreaEl && this.showSmartAppBanner) {
            this._bannerAreaEl.nativeElement.style.removeProperty('height');
            this.bannerOriginalHeight = this._bannerAreaEl.nativeElement.offsetHeight;
        }
        this.checkScrollPosition();
    }

    constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly awsAuth: AwsAuthService,
        private readonly translationService: TranslationService,
        private readonly languageService: LanguageService,
        private readonly _storiesService: MyStoriesService,
        private readonly renderer: Renderer2,
        private readonly _countrySelectionService: CountrySelectionService,
    ) {
        this.featureFlags = this.translationService.featureFlags;
        // This is done for CIAM related pages, where we have to show the header dynamic language.
        // This is set based on participant locale we get from API
        this.languageService.participantLocale.subscribe((locale: any) => {
            this.getUserSelectedLanguage(locale);
        });

        effect(() => {
            const openLangaugeSelector = this.languageService.getIsLanguageSelectionOpen();
            this.showBottomMenu = openLangaugeSelector;
        });
    }

    ngOnInit() {
        this.hideOrShowSmartBannerBasedOnRoute();
        this.language = this.languageService.getLanguage();
        const videoStatus = sessionStorage.getItem('videoplayed');
        if (videoStatus) {
            this.videoplayed = videoStatus;
        }
        if (this.videoplayed === 'true') {
            this.isHeaderMin = true;
        }
        this.handleFeatureFlags();
        this.handleProfileTabVisibilty();
        setTimeout(() => {
            this.isHeaderMin = true;
            this.getUserSelectedLanguage(this.language);
        }, 3600);

        const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
            debounceTime(100), // Debounce mouse moves to avoid excessive hiding
            filter(() => this.isAtTop), // Only consider mouse moves if the menu is currently visible
            map((event) => event.movementY), // Get the change in Y-coordinate
            filter((movementY) => movementY !== 0), // Only emit if there's actual vertical movement
            map((movementY) => (movementY < 0 ? 'up' : 'down')), // Map to 'up' or 'down'
            takeUntil(this.destroy$),
        );

        mouseMove$.subscribe((e: any) => {
            if (e !== 'up') {
                this.isAtTop = false;
            }
        });
    }

    private checkScrollPosition() {
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;

        // Banner collapse/expand based on scroll position
        // this.bannerOriginalHeight = this._bannerAreaEl?.nativeElement.offsetHeight;
        if (this.bannerOriginalHeight > 0 && this.showSmartAppBanner) {
            this.bannerCurrentHeight = Math.max(0, this.bannerOriginalHeight - currentScrollTop);
        }

        if (currentScrollTop === 0) {
            this.isSmartBannerVisible = true; // Menu should be visible
            this.isAtTop = true;
        } else if (currentScrollTop < this.lastScrollTop) {
            // Scrolling down AND past the header's initial height
            this.isSmartBannerVisible = false; // Menu should be hidden by scroll
            this.isAtTop = true;
        } else {
            this.isSmartBannerVisible = false;
            this.isAtTop = false;
        }
        // If scrolling up but not at 0, _isMenuHiddenByScroll retains its previous state.
        // This means the menu only reappears due to scroll when reaching the very top (scrollY === 0).

        this.lastScrollTop = currentScrollTop;
    }

    public hideOrShowSmartBannerBasedOnRoute(): void {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            const data = this.getActiveRouteData();
            this.bannerCurrentHeight = null;
            this.showSmartAppBanner = !data['hideSmartBanner'];
        });
    }

    private getActiveRouteData(): any {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route.data || {};
    }

    handleProfileTabVisibilty() {
        this.awsAuth.isLoginSuccess?.subscribe((isLoggedIn: boolean) => {
            this.currentUser = isLoggedIn || this.awsAuth.getUserLoggedInStatus();
            if (this.currentUser) {
                this.showMyProfileHeaderOnLogin();
            } else {
                this.showMyProfileLink = false;
                this.showLoginLink = true;
            }
            const currentUrl = this.location.path();
            this.selectedCategory = currentUrl.includes('/stories') ? '/stories' : '/home';
        });
    }

    public handleFeatureFlags() {
        this.isProd = this.featureFlags?.general?.tempProdFlag;
        this.isMyStoriesVisible = this.featureFlags?.circuitDetails.enableStories;
        this.enableMyProfile = this.featureFlags?.general.enableMyProfile;
        this.showSmartAppBanner = this.featureFlags?.general.enableSmartBanner;
        if (this.featureFlags?.general?.enableLanguageSelector) {
            this.isLanguageSelector = true;
        }
        if (this.featureFlags?.general.enableMyProfile) {
            this.isMyProfileVisible = true;
        }
        if (this.featureFlags?.circuitDetails.enableStories) {
            this.isHeadingAvailable = true;
        }
    }

    logout() {
        //Clear all cookie layer related variables from local storage
        localStorage.removeItem('uc_interaction_type');
        localStorage.removeItem('uc_services');
        localStorage.removeItem('uc_settings');
        localStorage.removeItem('uc_user_interaction');

        localStorage.removeItem('routeUrl');
        this.awsAuth.clearLocalStorageItems();
        localStorage.removeItem('countryOfOrigin');
        this.awsAuth.manageLogoutResponse();
    }

    openLanguagePicker() {
        this.languageService.showLanguageSelectionPage(true);
    }

    showDropMenu() {
        this.showMenu = true;
    }

    vidEnded() {
        sessionStorage.setItem('videoplayed', 'true');
    }

    hideMenuInMobile() {
        this.screenWidth = window.innerWidth;
        if (this.screenWidth < 1024) {
            this.showMenu = false;
        }
    }

    hideDropMenu() {
        this.showMenu = false;
    }

    showDropDownInMobile() {
        const isMenuOpen = !this.showBottomMenu;
        this.languageService.setIsLanguageSelectionOpen(isMenuOpen);
    }


    public reloadPage(): void {
        window.location.reload();
    }

    /**
     * @description If user lang is english but broswer language diff and is part of our supported lang - that lang.
     * If user lang and and browser default is Engligh -> German
     * If lang is not supported - then 404
     * @param locale
     */
    public getUserSelectedLanguage(participantLang: string) {
        let urlLang = participantLang;
        const supportedLocales: any[] = this.translationService.supportedLanguagesForDisplay;
        const fullURL = window.location.href.split('/');
        const langIndex = fullURL.findIndex((part) =>
            this.languageService.checkIsValideLanguage(part),
        );
        if (langIndex !== -1) {
            urlLang = fullURL[langIndex];
        }
        if (this.featureFlags?.general.enableLanguageSelector && supportedLocales.length) {
            this.languageToDisplay =
                supportedLocales.find((lang: { localeCode: string }) => lang.localeCode === urlLang)
                    ?.localeDescription ?? 'English';
            this.selectedLang = urlLang;
            this.language = urlLang;
        }
    }

    triggerLogin() {
        localStorage.removeItem('isLoggedIn');
        // this.awsAuth.manageLoginResponse();
        localStorage.removeItem('countryOfOrigin');
        this._countrySelectionService.triggerCountrySelection();
    }

    /**
     * @description Non-Prod:- Method to handle navigation to my stories page
     * We need to go to CIAM login page if user is not logged in
     * Upon logging in, I should be redirected to the same page I was last on
     * Prod:- Method to handle navigation to home page and no Login/Explore buttons visible to user
     */
    public handleMyStoriesNavigationClick(): void {
        // Initial setup actions, these remain the same.
        this.isProd = this.featureFlags?.general?.tempProdFlag;
        this.languageService.setIsLanguageSelectionOpen(false);
        this.showBottomMenu = false;
        this.renderer.removeStyle(document.body, 'overflow');

        const hasCountryOfOrigin = this._countrySelectionService.checkIfUserCountryOfOriginExists();

        // Define constants for route segments to avoid magic strings and improve readability.
        const STORIES_SEGMENT = 'stories';
        const HOME_SEGMENT = 'home';

        // Construct full paths using template literals for clarity.
        const storiesFullPath = `/${this.language}/${STORIES_SEGMENT}`;
        const homeFullPath = `/${this.language}/${HOME_SEGMENT}`;
        // Note: The original code used 'selectedLang' for localStorage, preserving that behavior.
        const localStorageStoriesFullPath = `/${this.selectedLang}/${STORIES_SEGMENT}`;

        // 2. Logic when 'isHeadingAvailable' is true.
        if (this.isHeadingAvailable) {
            // If the user is logged in AND has a country of origin, navigate directly to stories.
            // This handles the original `case this.currentUser && this.isHeadingAvailable && hasCountryOfOrigin:`
            if (this.currentUser && hasCountryOfOrigin) {
                this.router.navigateByUrl(storiesFullPath);
                this.selectedCategory = `/${STORIES_SEGMENT}`; // Update selectedCategory as per original logic.
            } else {
                // If 'isHeadingAvailable' is true, but either the user is NOT logged in
                // OR the country of origin is missing, then trigger login for stories.
                // This consolidates the original cases:
                // - `!this.currentUser && this.isHeadingAvailable && !hasCountryOfOrigin`
                // - `this.currentUser && this.isHeadingAvailable && !hasCountryOfOrigin`
                // - `!this.currentUser && this.isHeadingAvailable && hasCountryOfOrigin`
                localStorage.setItem('routeUrl', localStorageStoriesFullPath);
                this.triggerLogin();
            }
        } else {
            // 3. Logic when 'isHeadingAvailable' is false.
            // In this scenario, navigate to the home page.
            // This handles the original `case !this.currentUser && !this.isHeadingAvailable:`
            // and also covers any other scenarios that would have fallen into the `default:` case.
            this.router.navigateByUrl(homeFullPath);
        }
    }

    /**
     * @description Method to show/hide my profile and login links in header
     * called either on login or on page load
     */
    public showMyProfileHeaderOnLogin(): void {
        if (!this.currentUser) {
            this.showMyProfileLink = false;
            this.showLoginLink = true;
        } else if (this.currentUser && this.isHeadingAvailable) {
            this._storiesService.getProfile().subscribe({
                next: (data: ProfileDetails) => {
                    this._storiesService.ciamId = data.userId;
                    this._storiesService.profileInfoResponse = data;
                    this.profileInfo = data;
                    this.profileImage = data.profileImageUrl;
                    this.showMyProfileLink = true;
                    this.showLoginLink = false;
                },
                error: () => {
                    this.showMyProfileLink = false;
                    this.showLoginLink = false;
                    this.showLogout = true;
                },
            });
        }
    }

    profileClicked() {
        if (this.featureFlags?.general?.enableMyProfile) this.isProfileClicked = true;
    }
    cancelClicked(event: any) {
        if (event) {
            this.isProfileClicked = false;
        }
    }

    public navigateToProviderPage(): void {
        this.router.navigate(['/' + this.selectedLang + '/provider']);
        this.showDropDownInMobile();
    }

    public navigateToPrivacyPage(): void {
        this.router.navigate(['/' + this.selectedLang + '/privacy']);
        this.showDropDownInMobile();
    }
}
